import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import type { Trade } from "@/types/trade";

interface TradePickerProps {
  value: string[];
  onChange: (codes: string[]) => void;
  disabled?: boolean;
}

/**
 * Multi-select grid of all available trades, grouped by category.
 * Used in onboarding and in Settings → Trades.
 */
export function TradePicker({ value, onChange, disabled }: TradePickerProps) {
  const [trades, setTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from("trades")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      if (!error && data) setTrades(data as unknown as Trade[]);
      setLoading(false);
    })();
  }, []);

  const toggle = (code: string) => {
    if (value.includes(code)) {
      onChange(value.filter((c) => c !== code));
    } else {
      onChange([...value, code]);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Group by category
  const grouped = trades.reduce<Record<string, Trade[]>>((acc, t) => {
    (acc[t.category] ||= []).push(t);
    return acc;
  }, {});

  const categoryLabel: Record<string, string> = {
    exterior: "Exterior",
    mep: "Mechanical / Electrical / Plumbing",
    general: "General Construction",
    specialty: "Specialty",
  };

  return (
    <div className="space-y-6">
      {Object.entries(grouped).map(([cat, list]) => (
        <div key={cat} className="space-y-2">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {categoryLabel[cat] ?? cat}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {list.map((trade) => {
              const checked = value.includes(trade.code);
              return (
                <Card
                  key={trade.code}
                  onClick={() => !disabled && toggle(trade.code)}
                  className={`p-3 cursor-pointer transition-colors flex items-center gap-3 ${
                    checked ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                  } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <Checkbox checked={checked} disabled={disabled} className="pointer-events-none" />
                  <span className="text-sm font-medium">{trade.name}</span>
                </Card>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
